import type { JSONContent } from '@tiptap/core';

export interface ExtractedLink {
  pageId: string | null;
  target: string;
  kind: 'link' | 'embed';
  /** Plain text of the block the link sits in (for backlink previews). */
  context: string;
}

export interface Extracted {
  text: string;
  links: ExtractedLink[];
  tags: string[];
}

const BLOCK_SEPARATOR = '\n';

/** `![[file.png]]` points at an attachment, not a page. */
export const isAttachmentTarget = (target: string) =>
  /\.[a-z0-9]{2,5}$/i.test(target) && !/\.md$/i.test(target);

/** Plain text of an inline node, as a reader would see it. */
function inlineText(node: JSONContent): string {
  switch (node.type) {
    case 'text':
      return node.text ?? '';
    case 'wikiLink':
      return (node.attrs?.alias as string | null) ?? (node.attrs?.target as string) ?? '';
    case 'hashtag':
      return `#${node.attrs?.tag as string}`;
    case 'hardBreak':
      return '\n';
    case 'rawInline':
      return (node.attrs?.text as string) ?? '';
    default:
      return (node.content ?? []).map(inlineText).join('');
  }
}

const isTextblock = (n: JSONContent) =>
  n.type === 'paragraph' || n.type === 'heading' || n.type === 'codeBlock';

/**
 * Derive what the index needs from a page document: searchable text, outgoing
 * links (with context) and tags. Pure, so it runs in workers and tests.
 */
export function extractFromDoc(doc: JSONContent): Extracted {
  const blocks: string[] = [];
  const links: ExtractedLink[] = [];
  const tags: string[] = [];

  const walk = (node: JSONContent) => {
    if (node.type === 'embed') {
      const target = (node.attrs?.target as string) ?? '';
      blocks.push(`![[${target}]]`);
      if (isAttachmentTarget(target)) return;
      links.push({
        pageId: (node.attrs?.pageId as string | null) ?? null,
        target,
        kind: 'embed',
        context: '',
      });
      return;
    }
    if (node.type === 'rawBlock') {
      blocks.push((node.attrs?.text as string) ?? '');
      return;
    }
    if (isTextblock(node)) {
      const context = (node.content ?? []).map(inlineText).join('');
      blocks.push(context);
      for (const child of node.content ?? []) {
        // `[[#Heading]]` points inside the same page; not a link to another page.
        if (child.type === 'wikiLink' && child.attrs?.target) {
          links.push({
            pageId: (child.attrs?.pageId as string | null) ?? null,
            target: (child.attrs?.target as string) ?? '',
            kind: 'link',
            context: context.slice(0, 300),
          });
        } else if (child.type === 'hashtag') {
          tags.push(child.attrs?.tag as string);
        }
      }
      return;
    }
    node.content?.forEach(walk);
  };
  walk(doc);
  return { text: blocks.filter(Boolean).join(BLOCK_SEPARATOR), links, tags };
}
